import React, { useContext, useEffect, useRef, useState } from "react";
import { partners } from "../../lib/interfaces/IPartnerInterface";
import { MdKeyboardArrowDown, MdClose } from "react-icons/md";
import useUserLogin from "../../hooks/UserLoginhook";
import { AuthContext } from "../AuthContextComponent";

interface PartnersListDropdownProps {
  label: string;
  options: partners[];
  isRequired?: boolean;
  onSelect: (value: number | null) => void;
}

const PartnersListDropdown: React.FC<PartnersListDropdownProps> = ({
  label,
  options,
  isRequired,
  onSelect,
}) => {
  const { setPartnerName, userInfo } = useContext(AuthContext);
  const { customColor } = useUserLogin();

  const [isOpen, setIsOpen] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState<number>(-1);
  const [selected, setSelected] = useState<partners | null>(null);
  const [searchTerm, setSearchTerm] = useState("");

  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const optionRefs = useRef<(HTMLDivElement | null)[]>([]);

  const filteredOptions = options.filter((option) =>
    option.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelect = (option: partners) => {
    setSelected(option);
    setPartnerName(option.name);
    onSelect(option.id);
    setIsOpen(false);
    setHoveredIndex(-1);
    setSearchTerm("");
  };

  const clearSelection = () => {
    setSelected(null);
    setPartnerName("");
    onSelect(null);
    setSearchTerm("");
    setHoveredIndex(-1);
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Auto scroll highlighted option into view
  useEffect(() => {
    if (hoveredIndex >= 0 && optionRefs.current[hoveredIndex]) {
      optionRefs.current[hoveredIndex]?.scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      });
    }
  }, [hoveredIndex]);

  // Reset highlight when search changes
  useEffect(() => {
    if (filteredOptions.length === 0) {
      setHoveredIndex(-1);
    } else if (hoveredIndex >= filteredOptions.length || hoveredIndex === -1) {
      setHoveredIndex(0);
    }
  }, [searchTerm, filteredOptions, hoveredIndex]);

  const moveHighlight = (direction: "up" | "down") => {
    setHoveredIndex((prev) => {
      if (filteredOptions.length === 0) return -1;
      if (direction === "down") {
        return prev < filteredOptions.length - 1 ? prev + 1 : 0;
      }
      return prev > 0 ? prev - 1 : filteredOptions.length - 1;
    });
  };

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
      setIsOpen(true);
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      moveHighlight("down");
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      moveHighlight("up");
    } else if (
      e.key === "Enter" &&
      hoveredIndex >= 0 &&
      filteredOptions[hoveredIndex]
    ) {
      e.preventDefault();
      handleSelect(filteredOptions[hoveredIndex]);
    } else if (e.key === "Escape") {
      setIsOpen(false);
    } else if (e.key === "Tab") {
      setIsOpen(false);
    } else if (e.key === "Backspace" && searchTerm === "" && selected) {
      clearSelection();
      setIsOpen(true);
    }
  };

  return (
    <div
      className="relative items-center h-full pl-2 w-80"
      ref={dropdownRef}
      role="combobox"
      aria-expanded={isOpen}
      aria-haspopup="listbox"
      aria-owns="partner-listbox"
    >
      {/* Label */}
      <p
        className={`pl-2 mb-1 text-custom-micro ${
          userInfo?.theme === "DARK"
            ? "text-custom-dark-theme-text"
            : "text-black"
        }`}
        id="partner-label"
      >
        {label}
      </p>

      {/* Input box */}
      <div
        className={`flex items-center border rounded-md px-2 bg-white h-[30px] transition ${
          isRequired ? "border-red-600" : "border-gray-300"
        }`}
      >
        <input
          type="text"
          placeholder={selected ? selected.name : "SELECT A PARTNER"}
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            if (selected) clearSelection();
            setIsOpen(true);
          }}
          onClick={() => {
            setIsOpen(true);
            if (filteredOptions.length > 0) setHoveredIndex(0);
          }}
          onKeyDown={handleKeyDown}
          aria-autocomplete="list"
          aria-controls="partner-listbox"
          aria-activedescendant={
            hoveredIndex >= 0 && filteredOptions[hoveredIndex]
              ? `option-${filteredOptions[hoveredIndex].id}`
              : undefined
          }
          className="flex-1 h-[30px] bg-transparent focus:outline-none text-custom-petite placeholder:text-black"
        />

        {/* Action Icons */}
        <div className="flex items-center gap-1">
          {selected ? (
            <MdClose
              className="text-black cursor-pointer hover:text-red-500"
              onClick={(e) => {
                e.stopPropagation();
                clearSelection();
                setIsOpen(true);
              }}
              aria-label="Clear selection"
            />
          ) : (
            <MdKeyboardArrowDown
              className="text-gray-600 cursor-pointer hover:text-black"
              onClick={(e) => {
                e.stopPropagation();
                setIsOpen((prev) => !prev);
              }}
              aria-label="Toggle dropdown"
            />
          )}
        </div>
      </div>

      {/* Dropdown list */}
      {isOpen && (
        <div
          id="partner-listbox"
          className="absolute z-10 w-full mt-1 overflow-auto transition bg-white border border-gray-300 rounded-lg shadow-lg max-h-60"
          role="listbox"
        >
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => {
              const isActive = index === hoveredIndex;
              const isSelected = selected?.id === option.id;

              return (
                <div
                  key={option.id}
                  id={`option-${option.id}`}
                  ref={(el) => (optionRefs.current[index] = el)}
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => handleSelect(option)}
                  onMouseEnter={() => setHoveredIndex(index)}
                  className={`px-3 py-2 cursor-pointer text-sm rounded-md transition-colors ${
                    isActive
                      ? "text-white"
                      : isSelected
                      ? "bg-gray-100 font-medium"
                      : "text-gray-700"
                  }`}
                  style={{
                    backgroundColor: isActive ? customColor : undefined,
                  }}
                >
                  {option.name}
                </div>
              );
            })
          ) : (
            <div className="px-3 py-2 text-sm text-gray-500">
              No partners found for your search
            </div>
          )}
        </div>
      )}

      {/* Required note */}
      <div
        className={`text-custom-micro text-custom-default ${
          isRequired ? "block" : "invisible h-0"
        }`}
      >
        *Required
      </div>
    </div>
  );
};

export default PartnersListDropdown;
